import React from 'react';
import { Link } from 'react-router-dom';
import {
  Heart, ShieldPlus, User, Globe, HandHeart,
  Droplets, ArrowRight, Check, Gift, Users
} from 'lucide-react';
import { useSiteContent } from '../../hooks/useSiteContent';

const VALUE_ICONS = [ShieldPlus, User, HandHeart, Globe];
const COLOR_MAP = {
  blue:  { border: 'border-blue-400',  text: 'text-blue-400',  icon: 'text-blue-400' },
  pink:  { border: 'border-pink-400',  text: 'text-pink-400',  icon: 'text-pink-400' },
  green: { border: 'border-green-400', text: 'text-green-400', icon: 'text-green-400' },
};

const HygieneKitSection = ({ costBreakdown }) => {
  const { data: cms } = useSiteContent('hygiene_kit_section', {});
  const valuePoints = cms.value_points || [];
  const items = cms.items || [];
  const kits = cms.kits || [];
  const tiers = cms.impact_tiers || [];

  return (
    <section className="bg-[#0A0A0A] text-white rounded-lg overflow-hidden border border-[#1F1F1F]" data-testid="hygiene-kit-section">
      {/* Top: Title + description */}
      <div className="p-6 md:p-10">
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          <div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight leading-none">HYGIENE</h2>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight leading-none">
              <span className="text-[#BA1826]">ESSENTIALS</span> KIT
            </h2>
            {cms.description && (
              <p className="text-sm text-white/80 mt-5 leading-relaxed max-w-md">{cms.description}</p>
            )}
          </div>

          {/* Thoughtfully Curated Essentials */}
          <div>
            <h3 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-[#BA1826] text-center border-y border-[#BA1826]/50 py-2 mb-5">
              Thoughtfully Curated Essentials In Every Kit
            </h3>
            <div className="grid grid-cols-5 gap-2 text-center">
              {items.map((item, i) => (
                <div key={i} className="space-y-1.5" data-testid={`hygiene-item-${i}`}>
                  <div className="w-12 h-12 mx-auto rounded-full border border-[#BA1826] flex items-center justify-center">
                    <Droplets className="w-5 h-5 text-[#BA1826]" />
                  </div>
                  <p className="text-[9px] font-semibold uppercase tracking-wider leading-tight">{item.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Value points */}
        <div className="grid md:grid-cols-2 gap-5 mb-8">
          {valuePoints.map((vp, i) => {
            const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
            return (
              <div key={i} className="flex items-start gap-3" data-testid={`hygiene-value-${i}`}>
                <div className="w-10 h-10 rounded-full border border-[#BA1826] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#BA1826]" />
                </div>
                <div>
                  <h4 className="font-heading text-sm font-bold uppercase tracking-wide text-[#BA1826]">{vp.heading}</h4>
                  <p className="text-xs text-white/75 leading-relaxed mt-0.5">{vp.text}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* THREE KITS. THREE WAYS TO HELP. */}
        <div className="text-center mb-5">
          <h3 className="font-heading text-xs font-bold uppercase tracking-[0.3em] inline-block border-y border-[#BA1826]/50 py-2 px-6">
            <span className="text-white">Three Kits.</span> <span className="text-[#BA1826]">Three Ways To Help.</span>
          </h3>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {kits.map((kit, i) => {
            const c = COLOR_MAP[kit.color] || COLOR_MAP.blue;
            const donateAmount = Number(kit.donate_amount) || Number(kit.price) || 25;
            const donateLabel = kit.donate_label || `Fund a Kit — $${donateAmount}`;
            const kitSlug = encodeURIComponent(kit.name || 'Hygiene Kit');
            return (
              <div key={i} className={`border-2 ${c.border} rounded-lg p-4 bg-[#0A0A0A] flex flex-col`} data-testid={`hygiene-kit-${i}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <User className={`w-4 h-4 ${c.icon}`} />
                      <h4 className={`font-heading text-sm font-bold uppercase tracking-wide ${c.text}`}>{kit.name.replace(/Hygiene Kit/i, '').trim() || 'Kit'}</h4>
                    </div>
                    <p className={`text-[10px] font-semibold uppercase tracking-widest ${c.text} opacity-80`}>Hygiene Kit</p>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${c.text}`}>${kit.price}</div>
                    <div className="text-[9px] uppercase tracking-wider text-white/60">per kit</div>
                  </div>
                </div>
                <ul className="space-y-1.5 mb-3">
                  {(kit.items || []).map((it, j) => (
                    <li key={j} className="flex items-start gap-1.5 text-xs text-white/85">
                      <Check className={`w-3 h-3 ${c.icon} flex-shrink-0 mt-0.5`} strokeWidth={3} />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <p className={`text-[10px] font-bold uppercase tracking-wide ${c.text} pt-2 border-t border-white/10`}>
                  {kit.tagline}
                </p>
                {/* Kit-specific donate CTA */}
                <Link
                  to={`/donate?amount=${donateAmount}&kit=${kitSlug}`}
                  className={`mt-3 w-full inline-flex items-center justify-center gap-1.5 border ${c.border} ${c.text} hover:bg-[#BA1826] hover:border-[#BA1826] hover:text-white transition-colors text-[11px] font-bold uppercase tracking-wider px-3 py-2 rounded`}
                  data-testid={`hygiene-kit-donate-${i}`}
                >
                  <Heart className="w-3 h-3" />
                  <span>{donateLabel}</span>
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            );
          })}
        </div>

        {/* Your Impact + CTA row */}
        <div className="grid md:grid-cols-3 gap-4 mb-2">
          {/* Your Impact */}
          <div className="flex items-start gap-3" data-testid="hygiene-your-impact">
            <Gift className="w-8 h-8 text-[#BA1826] flex-shrink-0" />
            <div>
              <h4 className="font-heading text-xs font-bold uppercase tracking-wide text-[#BA1826]">Your Impact</h4>
              <p className="text-[11px] text-white/75 mt-0.5">Every kit you sponsor makes a direct difference in someone's life.</p>
            </div>
          </div>

          {/* Tiers */}
          <div className="grid grid-cols-2 gap-2">
            {tiers.map((t, i) => (
              <div key={i} className="flex items-start gap-2" data-testid={`hygiene-tier-${i}`}>
                <Users className="w-4 h-4 text-[#BA1826] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-heading text-sm font-bold">{t.kits} KITS</p>
                  <p className="text-[9px] text-white/70 leading-tight">{t.sub_label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="border border-[#BA1826] rounded-lg p-3 text-center" data-testid="hygiene-cta-block">
            <Heart className="w-6 h-6 text-[#BA1826] mx-auto mb-1" fill="#BA1826" />
            <p className="font-heading text-lg font-bold text-[#BA1826]">{cms.cta_amount_label}</p>
            <p className="text-[10px] font-semibold text-white/80 mb-2">{cms.cta_sub_text}</p>
            <Link to="/donate?amount=30" className="bg-[#BA1826] hover:bg-[#8B0F1C] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded inline-flex items-center gap-1" data-testid="hygiene-donate-btn">
              Donate <ArrowRight className="w-2.5 h-2.5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Transparency Breakdown */}
      {costBreakdown}
    </section>
  );
};

export default HygieneKitSection;
